import { useId } from "react"
import { useI18n } from "../../../i18n"
import { DEFAULT_PERSONA_NAME, PERSONA_SUBTITLE, PERSONA_SHORT_DESC } from "../../../brain/persona"
import { VOICE_GROUPS } from "../../../voice/voices"
import { ToggleSwitch } from "../ToggleSwitch"
import type { PanelProps } from "../types"

// Relocated from SettingsPanel.tsx's COMPANION, Appearance→language, and
// Voice sections — content and behaviour unchanged, only the section chrome
// (the old .settings-section-label headers duplicating what the surface's
// own "01 . COMPANION" heading already says) is trimmed.
const IDENTITY_DISPLAY: Record<string, string> = {
  rook: "Rook",
  rainer: "Rainer",
}

function identityDisplayName(slug: string): string {
  return IDENTITY_DISPLAY[slug] ?? slug.charAt(0).toUpperCase() + slug.slice(1)
}

export function CompanionPanel({
  prefs,
  updatePref,
  onRestartVoice,
  companionName = DEFAULT_PERSONA_NAME,
  companionSubtitle = PERSONA_SUBTITLE,
  companionShortDesc = PERSONA_SHORT_DESC,
  activeIdentity,
}: PanelProps) {
  const { t, locale, setLocale } = useI18n()
  const autoSpeakLabelId = useId()

  // activeIdentity is the slug of the chat Settings was opened from; fall
  // back to the server-sourced display name when it is absent.
  const scopeCompanion = activeIdentity ? identityDisplayName(activeIdentity) : companionName

  return (
    <>
      <div className="settings-row-hint" style={{ marginBottom: "var(--space-2)" }}>
        {t("settings.companion.scopeHint", { companion: scopeCompanion })}
      </div>

      <div className="companion-card">
        <div className="companion-card-name">{companionName}</div>
        <div className="companion-card-subtitle">{companionSubtitle}</div>
        <div className="companion-card-desc">{companionShortDesc}</div>
      </div>

      <div className="settings-row">
        <div className="settings-row-label">{t("settings.appearance.language")}</div>
        <div className="settings-segmented">
          <button
            type="button"
            className={locale === "en" ? "active" : ""}
            aria-pressed={locale === "en"}
            onClick={() => setLocale("en")}
          >
            EN
          </button>
          <button
            type="button"
            className={locale === "de" ? "active" : ""}
            aria-pressed={locale === "de"}
            onClick={() => setLocale("de")}
          >
            DE
          </button>
        </div>
      </div>

      <div className="settings-section">
        <div className="settings-section-label">{t("settings.voice")}</div>

        {/* Voice picker */}
        <div className="settings-row" style={{ flexDirection: "column", alignItems: "flex-start", gap: "var(--space-2)" }}>
          <div className="settings-row-label">{t("settings.voice.voice")}</div>
          <select
            className="settings-select"
            aria-label={t("settings.voice.voice")}
            value={prefs.voiceId}
            onChange={(e) => updatePref("voiceId", e.target.value)}
          >
            {VOICE_GROUPS.map(group => (
              <optgroup key={group.label} label={group.label}>
                {group.voices.map(v => (
                  <option key={v.id} value={v.id}>{v.label}</option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>

        {/* Auto-speak toggle */}
        <div className="settings-row">
          <div>
            <div className="settings-row-label" id={autoSpeakLabelId}>{t("settings.voice.autoSpeak")}</div>
            <div className="settings-row-hint">{t("settings.voice.autoSpeakHint")}</div>
          </div>
          <ToggleSwitch
            checked={prefs.autoSpeak}
            labelledBy={autoSpeakLabelId}
            onChange={() => updatePref("autoSpeak", !prefs.autoSpeak)}
          />
        </div>

        {/* Playback speed */}
        <div className="settings-row" style={{ flexDirection: "column", alignItems: "flex-start", gap: "var(--space-2)" }}>
          <div>
            <div className="settings-row-label">{t("settings.voice.speed")}</div>
            <div className="settings-row-hint">{t("settings.voice.speedHint")}</div>
          </div>
          <div className="settings-segmented">
            {[0.75, 1.0, 1.25, 1.5, 1.75, 2.0].map((s) => (
              <button
                type="button"
                key={s}
                className={prefs.playbackSpeed === s ? "active" : ""}
                aria-pressed={prefs.playbackSpeed === s}
                onClick={() => updatePref("playbackSpeed", s)}
                style={{ fontVariantNumeric: "tabular-nums" }}
              >
                {s}×
              </button>
            ))}
          </div>
        </div>

        {/* TTS engine selector */}
        <div className="settings-row">
          <div>
            <div className="settings-row-label">{t("settings.voice.ttsEngine")}</div>
            <div className="settings-row-hint">{t("settings.voice.ttsEngineHint")}</div>
          </div>
          <div className="settings-segmented">
            <button
              type="button"
              className={prefs.ttsMode === "server" ? "active" : ""}
              aria-pressed={prefs.ttsMode === "server"}
              onClick={() => updatePref("ttsMode", "server")}
            >
              {t("settings.voice.ttsServer")}
            </button>
            <button
              type="button"
              className={prefs.ttsMode === "browser" ? "active" : ""}
              aria-pressed={prefs.ttsMode === "browser"}
              onClick={() => updatePref("ttsMode", "browser")}
            >
              {t("settings.voice.ttsBrowser")}
            </button>
          </div>
        </div>

        {/* Restart voice engine */}
        <div className="settings-row">
          <div>
            <div className="settings-row-label">{t("settings.voice.engine")}</div>
            <div className="settings-row-hint">{t("settings.voice.engineHint")}</div>
          </div>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={onRestartVoice}
            style={{ fontSize: "var(--text-xs)" }}
          >
            Restart
          </button>
        </div>
      </div>
    </>
  )
}
